/**
 * Alert display helpers.
 *
 * Backend stores alert title and message as pre-rendered English strings for
 * storage + SMS/email fallback. The frontend re-renders them from `alert_type`
 * and the structured `trigger_data` so the user sees the alert in their
 * selected language. Falls back to the backend-supplied text whenever a key
 * or parameter is missing.
 */
import type { TFunction } from 'i18next';

// Alert types that benefit from showing the device name right in the title
// — operators need to know WHICH device at a glance, not just that something
// went wrong on this farm.
const DEVICE_ALERT_TYPES = new Set([
  'device_offline',
  'device_silent',
  'low_battery',
  'sensor_staleness',
  'faulty_sensor',
  'device_transferred',
  'device_released',
  'valve_command_timeout',
  'device_offline_during_command',
  'rule_sync_failed',
  'device_mac_storm',
  'valve_open_unconfirmed',
  'device_ignoring_commands',
  'controller_deaf',
  'valve_open_too_long',
]);

export function formatAlertTitle(
  t: TFunction,
  alertType: string | undefined,
  fallback: string,
  triggerData?: Record<string, any> | null,
): string {
  if (!alertType) return fallback;
  const key = `types.${alertType}`;
  const translated = t(key, { ns: 'alerts', defaultValue: '' });
  const base = translated || fallback;

  // Append the device label for device-related alerts so the operator can
  // identify the affected device without opening the details. Backend already
  // includes ``device_name`` (or dev_eui) in trigger_data for these types.
  if (triggerData && DEVICE_ALERT_TYPES.has(alertType)) {
    const deviceName = (
      triggerData.device_name
      ?? triggerData.deviceName
      ?? triggerData.dev_eui
      ?? triggerData.devEui
    );
    if (deviceName && !base.includes(String(deviceName))) {
      return `${base}: ${deviceName}`;
    }
  }
  return base;
}

interface AlertForFormat {
  alert_type?: string;
  alertType?: string;
  message?: string;
  trigger_data?: Record<string, any> | null;
  triggerData?: Record<string, any> | null;
  triggered_at?: string;
  triggeredAt?: string;
}

function durationFromMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function durationFromSeconds(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return durationFromMinutes(seconds / 60);
}

/** Pull a human-friendly device label out of trigger_data, prefer name over EUI. */
function getDeviceName(triggerData: Record<string, any>): string {
  return (
    triggerData.device_name
    ?? triggerData.deviceName
    ?? triggerData.dev_eui
    ?? triggerData.devEui
    ?? ''
  );
}

function formatDateTime(iso: string | undefined, lang: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(lang);
  } catch {
    return iso;
  }
}

function formatDate(iso: string | undefined, lang: string): string {
  if (!iso) return '';
  try {
    // Dates may be YYYY-MM-DD; treat as local to avoid TZ shifting.
    const d = iso.length === 10 ? new Date(iso + 'T00:00:00') : new Date(iso);
    return d.toLocaleDateString(lang);
  } catch {
    return iso;
  }
}

export function formatAlertMessage(
  t: TFunction,
  lang: string,
  alert: AlertForFormat,
): string {
  const alertType = alert.alert_type ?? alert.alertType;
  const triggerData = alert.trigger_data ?? alert.triggerData ?? {};
  const fallback = alert.message ?? '';
  if (!alertType) return fallback;

  const farmName = triggerData.farm_name ?? triggerData.farmName ?? '';

  const deviceName = getDeviceName(triggerData);

  switch (alertType) {
    case 'low_battery': {
      const percent = triggerData.battery_percent ?? triggerData.batteryPercent;
      if (percent === undefined || !farmName) return fallback;
      return t('messages.low_battery', { ns: 'alerts', farmName, deviceName, percent });
    }
    case 'device_offline': {
      const minutesAgo = triggerData.minutes_ago ?? triggerData.minutesAgo;
      const lastSeenIso = triggerData.last_seen ?? triggerData.lastSeen;
      if (minutesAgo === undefined || !farmName) return fallback;
      return t('messages.device_offline', {
        ns: 'alerts',
        farmName,
        deviceName,
        duration: durationFromMinutes(Number(minutesAgo)),
        lastSeen: formatDateTime(lastSeenIso, lang),
      });
    }
    case 'device_silent': {
      // The adaptive silence detector raises this for TTN sensors that
      // missed multiple expected uplinks. See alert_generation_service.
      const silentSeconds = triggerData.silent_seconds ?? triggerData.silentSeconds;
      const expectedSeconds = triggerData.expected_interval_seconds ?? triggerData.expectedIntervalSeconds;
      const ratio = triggerData.silence_ratio ?? triggerData.silenceRatio;
      const macFrames = triggerData.mac_only_frames ?? triggerData.macOnlyFrames;
      if (silentSeconds === undefined || !farmName) return fallback;
      // A device whose radio kept transmitting MAC-only frames is stuck, not
      // out of range — different message, different field action.
      const key = macFrames ? 'messages.device_silent_stuck' : 'messages.device_silent';
      return t(key, {
        ns: 'alerts',
        farmName,
        deviceName,
        silentDuration: durationFromSeconds(Number(silentSeconds)),
        expectedDuration: expectedSeconds !== undefined
          ? durationFromSeconds(Number(expectedSeconds))
          : '—',
        ratio: ratio !== undefined ? Number(ratio).toFixed(1) : '?',
      });
    }
    case 'flow_meter_silent_under_pressure': {
      const sectors = triggerData.sector_names ?? triggerData.sectorNames;
      const kpa = triggerData.pressure_kpa ?? triggerData.pressureKpa;
      const devEui = triggerData.dev_eui ?? triggerData.devEui;
      if (!Array.isArray(sectors) || kpa === undefined || !farmName) return fallback;
      return t('messages.flow_meter_silent_under_pressure', {
        ns: 'alerts',
        farmName,
        sectors: sectors.join(', '),
        bar: (Number(kpa) / 100).toFixed(1),
        devEui: devEui ?? '?',
      });
    }
    case 'device_mac_storm': {
      const macFrames = triggerData.mac_frames ?? triggerData.macFrames;
      const windowMinutes = triggerData.window_minutes ?? triggerData.windowMinutes;
      if (macFrames === undefined || !farmName) return fallback;
      return t('messages.device_mac_storm', {
        ns: 'alerts',
        farmName,
        deviceName,
        frames: Number(macFrames),
        minutes: windowMinutes !== undefined ? Number(windowMinutes) : 15,
      });
    }
    case 'device_ignoring_commands': {
      const uplinks = triggerData.uplinks_since ?? triggerData.uplinksSince;
      const minutes = triggerData.ignored_minutes ?? triggerData.ignoredMinutes;
      const valve = triggerData.valve_number ?? triggerData.valveNumber;
      const action = triggerData.action;
      const reported = triggerData.reported_status ?? triggerData.reportedStatus;
      if (uplinks === undefined || !farmName || !action) return fallback;
      return t(`messages.device_ignoring_commands_${action === 'close' ? 'close' : 'open'}`, {
        ns: 'alerts',
        farmName,
        deviceName,
        uplinks: Number(uplinks),
        minutes: minutes !== undefined ? Number(minutes) : '?',
        valve: valve ?? 1,
        reported: reported ?? '?',
      });
    }
    case 'controller_deaf': {
      const ignored = Number(triggerData.ignored_commands ?? triggerData.ignoredCommands ?? 0);
      const pumpCmds = Number(triggerData.ignored_pump_commands ?? triggerData.ignoredPumpCommands ?? 0);
      const silent = triggerData.state_silent_minutes ?? triggerData.stateSilentMinutes;
      if (!farmName) return fallback;
      const key = pumpCmds > 0
        ? 'messages.controller_deaf_pump'
        : ignored > 0
          ? 'messages.controller_deaf_commands'
          : 'messages.controller_deaf_frozen';
      return t(key, {
        ns: 'alerts', farmName, deviceName, ignored,
        minutes: silent !== undefined && silent !== null ? Number(silent) : '?',
      });
    }
    case 'valve_open_too_long': {
      const hours = triggerData.open_hours ?? triggerData.openHours;
      const valve = triggerData.valve_number ?? triggerData.valveNumber;
      if (hours === undefined || !farmName) return fallback;
      return t('messages.valve_open_too_long', {
        ns: 'alerts', farmName, deviceName, hours: Number(hours), valve: valve ?? 1,
      });
    }
    case 'valve_open_unconfirmed': {
      const openMinutes = triggerData.open_minutes ?? triggerData.openMinutes;
      const blindSeconds = triggerData.blind_seconds ?? triggerData.blindSeconds;
      const overdueMinutes = triggerData.overdue_minutes ?? triggerData.overdueMinutes;
      const programName = triggerData.program_name ?? triggerData.programName;
      const valveName = triggerData.valve_name ?? triggerData.valveName
        ?? `V${triggerData.valve_number ?? triggerData.valveNumber ?? '?'}`;
      if (openMinutes === undefined || !farmName) return fallback;
      // Overdue (we know when it should have closed) reads better than a raw
      // blind duration, so prefer it when both apply.
      if (overdueMinutes !== undefined && overdueMinutes !== null) {
        return t('messages.valve_open_unconfirmed_overdue', {
          ns: 'alerts',
          farmName,
          deviceName,
          valveName,
          programName: programName ?? '—',
          overdueDuration: durationFromMinutes(Number(overdueMinutes)),
        });
      }
      return t('messages.valve_open_unconfirmed_blind', {
        ns: 'alerts',
        farmName,
        deviceName,
        valveName,
        openDuration: durationFromMinutes(Number(openMinutes)),
        blindDuration: durationFromSeconds(Number(blindSeconds ?? 0)),
      });
    }
    case 'water_stress': {
      const days = triggerData.days_until_stress ?? triggerData.daysUntilStress;
      const proj = triggerData.projected_depletion_percent ?? triggerData.projectedDepletionPercent;
      if (days === undefined || !farmName) return fallback;
      const percent = proj !== undefined ? Math.round(Number(proj)) : 0;
      return t('messages.water_stress', { ns: 'alerts', farmName, days, percent });
    }
    case 'sensor_staleness': {
      const hours = triggerData.hours_since_data ?? triggerData.hoursSinceData;
      if (hours === undefined || !farmName) return fallback;
      return t('messages.sensor_staleness', {
        ns: 'alerts',
        farmName,
        deviceName,
        hours: Number(hours).toFixed(1),
      });
    }
    case 'program_failure': {
      const programName = triggerData.program_name ?? triggerData.programName;
      const runStatus = triggerData.run_status ?? triggerData.runStatus;
      const dateRaw = triggerData.execution_date ?? triggerData.executionDate;
      const errorMsg = triggerData.error_message ?? triggerData.errorMessage;
      if (!programName || !runStatus || !farmName) return fallback;
      const statusKey = String(runStatus).toLowerCase();
      const status = t(`runStatus.${statusKey}`, {
        ns: 'alerts',
        defaultValue: statusKey,
      });
      const date = formatDate(dateRaw, lang);
      if (errorMsg) {
        return t('messages.program_failure', {
          ns: 'alerts',
          farmName,
          programName,
          status,
          date,
          error: errorMsg,
        });
      }
      return t('messages.program_failure_no_error', {
        ns: 'alerts',
        farmName,
        programName,
        status,
        date,
      });
    }
    default:
      return fallback;
  }
}
